// Adapted from herdr VoiceInput.tsx, Copyright (c) 2026 devswha (MIT).
import { useEffect, useRef, useState, type RefObject } from 'react';
import { VoiceKeyReq } from '@pen/shared';
import { request } from '../api';
import { applyDictation, classifyRelease, type DictationSpans, type VoiceText } from './helpers';
import { useVoiceCapture } from './hooks';
import { useVoiceStatus, VOICE_CONFIG_EVENT } from './status';
import './voice.css';

/** Dictation only inserts/replaces text; submitting the message remains the caller's action. */
export function ChatVoiceInput(props: {
  lessonId?: string;
  box: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange(value: string, caret: number): void;
  disabled?: boolean;
  onBusyChange?(busy:boolean):void;
}) {
  const latest = useRef(props);
  latest.current = props;
  const spans = useRef<DictationSpans>(new Map());
  const live = useRef(true);
  const composing = useRef(false);
  const waiting = useRef<VoiceText[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [note, setNote] = useState<string | null>(null);
  const apply = useRef((result: VoiceText): void => {
    if (!live.current) return;
    if (composing.current) { waiting.current.push(result); return; }
    const { box, onChange } = latest.current;
    const element = box.current;
    // Use the live textarea between consecutive NDJSON events, before React commits its render.
    const value = element?.value ?? latest.current.value;
    const selection = element ? { start: element.selectionStart, end: element.selectionEnd } : { start: value.length, end: value.length };
    const next = applyDictation(value, selection, spans.current, result, element && element.maxLength >= 0 ? element.maxLength : Infinity);
    if (next === 'too_long') { setNote('The dictation does not fit in the message.'); return; }
    if (!next) return;
    if (element) {
      element.value = next.value;
      element.setSelectionRange(next.caret, next.caret);
    }
    onChange(next.value, next.caret);
  });
  const voice = useVoiceCapture({
    lessonId: props.lessonId, now: () => performance.now(), continuous: false,
    onText: (result) => { if (latest.current.lessonId === props.lessonId) apply.current(result); },
  });
  const pressedAt = useRef<number | null>(null);
  const pointerUpAt = useRef(-Infinity);
  useEffect(() => { props.onBusyChange?.(voice.active || voice.busy); }, [voice.active, voice.busy, props.onBusyChange]);
  const startedOnPress = useRef(false);
  useEffect(() => {
    live.current = true;
    const element = props.box.current;
    const startComposition = (): void => { composing.current = true; };
    const endComposition = (): void => {
      composing.current = false;
      flushTimer.current = setTimeout(() => {
        flushTimer.current = undefined;
        if (composing.current || !live.current) return;
        for (const result of waiting.current.splice(0)) apply.current(result);
      }, 0);
    };
    // Even an edit subsequently undone invalidates polish: matching text alone is insufficient.
    const edited = (): void => { spans.current.clear(); spans.current.box = element?.value; };
    element?.addEventListener('compositionstart', startComposition);
    element?.addEventListener('compositionend', endComposition);
    element?.addEventListener('input', edited);
    return () => {
      live.current = false;
      waiting.current = [];
      clearTimeout(flushTimer.current);
      element?.removeEventListener('compositionstart', startComposition);
      element?.removeEventListener('compositionend', endComposition);
      element?.removeEventListener('input', edited);
    };
  }, [props.box]);
  useEffect(() => { if (props.disabled) void voice.stop(); }, [props.disabled, voice.stop]);
  useEffect(() => {
    if (!voice.active) return;
    const escape = (event: KeyboardEvent): void => { if (event.key === 'Escape') { event.preventDefault(); void voice.stop(); } };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [voice.active, voice.stop]);
  const release = (): void => {
    pointerUpAt.current = performance.now();
    if (pressedAt.current !== null && startedOnPress.current && classifyRelease(pointerUpAt.current - pressedAt.current) === 'finish') void voice.stop();
    pressedAt.current = null;
  };
  return <span className="voice-chat">
    <button type="button" className="voice-mic" aria-label={voice.active ? 'Insert dictation' : 'Dictate message'} aria-pressed={voice.active}
      disabled={props.disabled || (voice.busy && !voice.active)}
      onPointerDown={(event) => {
        if (event.button !== 0 || props.disabled) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        pressedAt.current = performance.now();
        startedOnPress.current = !voice.active;
        setNote(null);
        voice.toggle();
      }}
      onPointerUp={release}
      onPointerCancel={() => { pressedAt.current = null; pointerUpAt.current = performance.now(); void voice.stop(); }}
      onLostPointerCapture={() => { if (pressedAt.current !== null) release(); }}
      onClick={(event) => {
        if (performance.now() - pointerUpAt.current < 600) return;
        event.preventDefault();
        setNote(null);
        voice.toggle();
      }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3m-4 0h8"/></svg>
      <span ref={voice.levelRef} className="voice-meter" aria-hidden="true" />
    </button>
    {(voice.active || voice.busy) && <span className="voice-note" role="status">{voice.starting ? 'Opening microphone…' : voice.active ? 'Listening…' : 'Transcribing…'}</span>}
    {(voice.error || note) && <span className="voice-error" role="alert">{voice.error || note}</span>}
  </span>;
}

export function VoiceSettings() {
  const status = useVoiceStatus();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  async function changeKey(remove: boolean): Promise<void> {
    setError(null);
    setSaved(false);
    if (!remove) {
      const parsed = VoiceKeyReq.safeParse({ apiKey: key.trim() });
      if (!parsed.success) { setError('Enter a valid OpenRouter API key (20–300 characters).'); return; }
    }
    setBusy(true);
    try {
      await request<void>(remove ? 'DELETE' : 'PUT', '/api/voice/key', remove ? undefined : { apiKey: key.trim() });
      setKey('');
      setSaved(true);
      window.dispatchEvent(new Event(VOICE_CONFIG_EVENT));
    } catch (failure) { setError(failure instanceof Error ? failure.message : 'Unable to update the voice key.'); }
    finally { setBusy(false); }
  }
  return <section className="voice-settings" aria-labelledby="voice-settings-heading">
    <h2 id="voice-settings-heading">Voice input</h2>
    <p>Dictate messages or think aloud while working on a page. Nothing is sent as a chat message automatically.</p>
    <p>Recorded audio is transcribed through OpenRouter, then lightly cleaned up. Browser speech recognition is never used.</p>
    <p role="status">{status ? status.hasKey ? `OpenRouter transcription and cleanup are configured${status.source === 'server' ? ' by the server' : ''}.` : 'Voice is not configured. Add an OpenRouter key below.' : 'Loading voice status…'}</p>
    <form onSubmit={(event) => { event.preventDefault(); void changeKey(false); }}>
      <label htmlFor="voice-api-key">OpenRouter API key</label>
      <input id="voice-api-key" type="password" autoComplete="off" spellCheck={false} value={key} onChange={(event) => { setKey(event.target.value); setSaved(false); }} placeholder={status?.hasKey ? 'Replace saved key' : 'sk-or-v1-…'} disabled={busy} />
      <p className="voice-note">Your key is validated, then encrypted on the server. Recordings go to OpenRouter when you use the microphone. Your personal key, if set, overrides the server voice key.</p>
      <div className="voice-settings-actions"><button disabled={busy || !key.trim()}>{busy ? 'Saving…' : 'Save key'}</button>
        {status?.source === 'personal' && <button type="button" disabled={busy} onClick={() => { void changeKey(true); }}>Remove key</button>}</div>
    </form>
    {error && <p role="alert" className="voice-error">{error}</p>}
    {saved && <p role="status">Voice key updated.</p>}
  </section>;
}
